// Self-service profile ("My profile"). Read-only identity/contact via the
// shared PersonDetailView (no history, no government-ID reveal), plus an inline
// "Edit contact info" mode that edits ONLY the self-editable contact categories
// (addresses / phones / personal emails / emergency contacts) and submits an
// UpdateMyContactInput (replace-all per category) through useUpdateMyContact.
//
// We do NOT reuse PersonForm here: that form also edits identity (name/DOB/
// physical) which is not self-editable. The edit UI below mirrors PersonForm's
// row-list patterns but is scoped to the contact categories only.

import { useState, type FormEvent } from 'react';
import type {
  CreatePersonAddressInput,
  CreatePersonEmailInput,
  CreatePersonEmergencyContactInput,
  CreatePersonPhoneInput,
  EmergencyRelationship,
  PersonAddressLabel,
  PersonDetail,
  PersonEmailLabel,
  PersonPhoneLabel,
  UpdateMyContactInput,
} from '@dsr/shared-types';
import { formatPhoneDisplay } from '@dsr/shared-types';
import { useMyProfile, useUpdateMyContact } from '../api/queries/self';
import { PersonDetailView } from '../components/PersonDetailView';
import { PhoneInput } from '../components/PhoneInput';
import { ApiError } from '../api/client';
import { toast } from '../components/Toasts';
import { fmtDate } from '../utils/date';
import { Button, FormField, FormRow, Input } from '../components/ui';

// ---------- Enum option tables (contact categories only) ----------

const ADDRESS_LABEL_OPTIONS: { value: PersonAddressLabel; label: string }[] = [
  { value: 'home', label: 'Home' },
  { value: 'mailing', label: 'Mailing' },
  { value: 'work', label: 'Work' },
  { value: 'residence', label: 'Residence' },
  { value: 'previous', label: 'Previous' },
  { value: 'other', label: 'Other' },
];

const PHONE_LABEL_OPTIONS: { value: PersonPhoneLabel; label: string }[] = [
  { value: 'mobile', label: 'Mobile' },
  { value: 'home', label: 'Home' },
  { value: 'work', label: 'Work' },
  { value: 'other', label: 'Other' },
];

const EMAIL_LABEL_OPTIONS: { value: PersonEmailLabel; label: string }[] = [
  { value: 'personal', label: 'Personal' },
  { value: 'work', label: 'Work' },
  { value: 'company', label: 'Company' },
  { value: 'other', label: 'Other' },
];

const RELATIONSHIP_OPTIONS: { value: EmergencyRelationship; label: string }[] = [
  { value: 'spouse', label: 'Spouse' },
  { value: 'parent', label: 'Parent' },
  { value: 'child', label: 'Child' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'partner', label: 'Partner' },
  { value: 'friend', label: 'Friend' },
  { value: 'other', label: 'Other' },
];

const EMPLOYMENT_LABEL: Record<string, string> = {
  full_time: 'Full-time',
  part_time: 'Part-time',
  contractor: 'Contractor',
  temp: 'Temporary',
};

// ---------- Editable row shapes (local form state) ----------

interface AddrRow {
  _key: string;
  label: PersonAddressLabel;
  street1: string;
  street2: string;
  city: string;
  state: string;
  postal_code: string;
  is_primary: boolean;
}

interface PhoneRow {
  _key: string;
  label: PersonPhoneLabel;
  number: string;
  extension: string;
  is_primary: boolean;
}

interface EmailRow {
  _key: string;
  label: PersonEmailLabel;
  email: string;
  is_primary: boolean;
  is_company_managed: boolean;
}

interface ContactRow {
  _key: string;
  first_name: string;
  last_name: string;
  relationship: EmergencyRelationship;
  phone: string;
  alt_phone: string;
  email: string;
  priority: string;
}

let keySeq = 0;
function nextKey(): string {
  keySeq += 1;
  return `r${keySeq}`;
}

function emptyAddr(): AddrRow {
  return {
    _key: nextKey(),
    label: 'home',
    street1: '',
    street2: '',
    city: '',
    state: '',
    postal_code: '',
    is_primary: false,
  };
}
function emptyPhone(): PhoneRow {
  return { _key: nextKey(), label: 'mobile', number: '', extension: '', is_primary: false };
}
function emptyEmail(): EmailRow {
  return {
    _key: nextKey(),
    label: 'personal',
    email: '',
    is_primary: false,
    is_company_managed: false,
  };
}
function emptyContact(): ContactRow {
  return {
    _key: nextKey(),
    first_name: '',
    last_name: '',
    relationship: 'spouse',
    phone: '',
    alt_phone: '',
    email: '',
    priority: '1',
  };
}

// ===========================================================================
// Page
// ===========================================================================

export function MyProfilePage(): JSX.Element {
  const { data, isPending, isError, error } = useMyProfile();
  const [editing, setEditing] = useState(false);

  if (isPending) {
    return <div className="page-state">Loading your profile…</div>;
  }
  if (isError || !data) {
    return (
      <div className="page-state page-error" role="alert">
        {error instanceof ApiError ? error.message : 'Failed to load your profile.'}
      </div>
    );
  }

  const { person, personnel } = data;

  return (
    <div className="page my-profile-page">
      <header className="page-header">
        <div>
          <h1>My profile</h1>
          <p className="page-sub">Your identity, contact details, and HR record.</p>
        </div>
        {person && !editing && (
          <div className="header-actions">
            <Button variant="primary" onClick={() => setEditing(true)}>
              Edit contact info
            </Button>
          </div>
        )}
      </header>

      {personnel && (
        <section className="detail-card">
          <h2>Employment</h2>
          <dl className="detail-grid">
            <div>
              <dt>Position</dt>
              <dd>{personnel.position_title ?? '—'}</dd>
            </div>
            <div>
              <dt>Department</dt>
              <dd>{personnel.department ?? '—'}</dd>
            </div>
            <div>
              <dt>Employment type</dt>
              <dd>{EMPLOYMENT_LABEL[personnel.employment_type] ?? personnel.employment_type}</dd>
            </div>
            <div>
              <dt>Hire date</dt>
              <dd>{fmtDate(personnel.hire_date)}</dd>
            </div>
            <div>
              <dt>Supervisor</dt>
              <dd>
                {personnel.supervisor_personnel_id != null
                  ? `#${personnel.supervisor_personnel_id}`
                  : '—'}
              </dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd className="capitalize">{personnel.status}</dd>
            </div>
          </dl>
        </section>
      )}

      {!person ? (
        <div className="page-state">
          No identity record is linked to your account.
        </div>
      ) : editing ? (
        <ContactEditForm
          person={person}
          onDone={() => setEditing(false)}
        />
      ) : (
        <PersonDetailView person={person} />
      )}
    </div>
  );
}

// ===========================================================================
// Inline contact editor (self.update_contact — replace-all per category)
// ===========================================================================

function ContactEditForm({
  person,
  onDone,
}: {
  person: PersonDetail;
  onDone: () => void;
}): JSX.Element {
  const update = useUpdateMyContact();

  const [addresses, setAddresses] = useState<AddrRow[]>(
    (person.addresses ?? []).map((a) => ({
      _key: nextKey(),
      label: a.label,
      street1: a.street1,
      street2: a.street2 ?? '',
      city: a.city,
      state: a.state ?? '',
      postal_code: a.postal_code ?? '',
      is_primary: a.is_primary,
    })),
  );
  const [phones, setPhones] = useState<PhoneRow[]>(
    (person.phones ?? []).map((p) => ({
      _key: nextKey(),
      label: p.label,
      number: formatPhoneDisplay(p.number),
      extension: p.extension ?? '',
      is_primary: p.is_primary,
    })),
  );
  // Company-managed email rows are preserved server-side regardless of the
  // payload, so we only surface personal-labeled (non-managed) rows for edit.
  const [emails, setEmails] = useState<EmailRow[]>(
    (person.emails ?? [])
      .filter((e) => !e.is_company_managed)
      .map((e) => ({
        _key: nextKey(),
        label: e.label,
        email: e.email,
        is_primary: e.is_primary,
        is_company_managed: e.is_company_managed,
      })),
  );
  const managedEmails = (person.emails ?? []).filter((e) => e.is_company_managed);
  const [contacts, setContacts] = useState<ContactRow[]>(
    (person.emergency_contacts ?? []).map((c) => ({
      _key: nextKey(),
      first_name: c.first_name,
      last_name: c.last_name,
      relationship: c.relationship,
      phone: formatPhoneDisplay(c.phone),
      alt_phone: c.alt_phone ? formatPhoneDisplay(c.alt_phone) : '',
      email: c.email ?? '',
      priority: String(c.priority),
    })),
  );
  const [formError, setFormError] = useState<string | null>(null);

  const patchAddr = (key: string, patch: Partial<AddrRow>): void =>
    setAddresses((rows) => rows.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  const patchPhone = (key: string, patch: Partial<PhoneRow>): void =>
    setPhones((rows) => rows.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  const patchEmail = (key: string, patch: Partial<EmailRow>): void =>
    setEmails((rows) => rows.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  const patchContact = (key: string, patch: Partial<ContactRow>): void =>
    setContacts((rows) => rows.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  const setAddrPrimary = (key: string): void =>
    setAddresses((rows) => rows.map((r) => ({ ...r, is_primary: r._key === key })));
  const setPhonePrimary = (key: string): void =>
    setPhones((rows) => rows.map((r) => ({ ...r, is_primary: r._key === key })));
  const setEmailPrimary = (key: string): void =>
    setEmails((rows) => rows.map((r) => ({ ...r, is_primary: r._key === key })));

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setFormError(null);

    const addressPayload: CreatePersonAddressInput[] = addresses
      .filter((a) => a.street1.trim() || a.city.trim())
      .map((a) => ({
        label: a.label,
        street1: a.street1.trim(),
        city: a.city.trim(),
        ...(a.street2.trim() ? { street2: a.street2.trim() } : {}),
        ...(a.state.trim() ? { state: a.state.trim() } : {}),
        ...(a.postal_code.trim() ? { postal_code: a.postal_code.trim() } : {}),
        is_primary: a.is_primary,
      }));

    const phonePayload: CreatePersonPhoneInput[] = phones
      .filter((p) => p.number.trim())
      .map((p) => ({
        label: p.label,
        number: p.number.trim(),
        ...(p.extension.trim() ? { extension: p.extension.trim() } : {}),
        is_primary: p.is_primary,
      }));

    const emailPayload: CreatePersonEmailInput[] = emails
      .filter((e2) => e2.email.trim())
      .map((e2) => ({
        label: e2.label,
        email: e2.email.trim(),
        is_primary: e2.is_primary,
      }));

    let contactInvalid = false;
    const contactPayload: CreatePersonEmergencyContactInput[] = contacts
      .filter((c) => c.first_name.trim() || c.phone.trim())
      .map((c) => {
        if (!c.first_name.trim() || !c.phone.trim()) contactInvalid = true;
        const pr = Number(c.priority);
        return {
          first_name: c.first_name.trim(),
          last_name: c.last_name.trim(),
          relationship: c.relationship,
          phone: c.phone.trim(),
          ...(c.alt_phone.trim() ? { alt_phone: c.alt_phone.trim() } : {}),
          ...(c.email.trim() ? { email: c.email.trim() } : {}),
          ...(Number.isInteger(pr) && pr > 0 ? { priority: pr } : {}),
        };
      });
    if (contactInvalid) {
      setFormError('Each emergency contact needs at least a first name and phone.');
      return;
    }

    const input: UpdateMyContactInput = {
      addresses: addressPayload,
      phones: phonePayload,
      emails: emailPayload,
      emergency_contacts: contactPayload,
    };

    update.mutate(input, {
      onSuccess: () => {
        toast.success('Contact info updated.');
        onDone();
      },
      onError: (err) =>
        setFormError(err instanceof ApiError ? err.message : 'Could not save your contact info.'),
    });
  };

  return (
    <form className="person-form" onSubmit={handleSubmit}>
      {/* ---------- Addresses ---------- */}
      <section className="detail-card">
        <div className="section-header">
          <h2>Addresses</h2>
          <Button
            variant="primary"
            className="btn-sm"
            onClick={() => setAddresses((rows) => [...rows, emptyAddr()])}
          >
            Add address
          </Button>
        </div>
        {addresses.length === 0 && <p className="muted">No addresses added.</p>}
        {addresses.map((a) => (
          <div key={a._key} className="contact-row">
            <FormRow>
              <FormField label="Label">
                <select
                  className="form-input"
                  value={a.label}
                  onChange={(e) => patchAddr(a._key, { label: e.target.value as PersonAddressLabel })}
                >
                  {ADDRESS_LABEL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Primary">
                <span className="checkbox-field">
                  <input
                    type="radio"
                    name="addr-primary"
                    checked={a.is_primary}
                    onChange={() => setAddrPrimary(a._key)}
                  />
                  <span>Primary address</span>
                </span>
              </FormField>
              <FormField label="Street" full>
                <Input
                  value={a.street1}
                  onChange={(e) => patchAddr(a._key, { street1: e.target.value })}
                />
              </FormField>
              <FormField label="Street 2" full>
                <Input
                  value={a.street2}
                  onChange={(e) => patchAddr(a._key, { street2: e.target.value })}
                />
              </FormField>
              <FormField label="City">
                <Input
                  value={a.city}
                  onChange={(e) => patchAddr(a._key, { city: e.target.value })}
                />
              </FormField>
              <FormField label="State">
                <Input
                  value={a.state}
                  maxLength={2}
                  onChange={(e) => patchAddr(a._key, { state: e.target.value.toUpperCase() })}
                />
              </FormField>
              <FormField label="ZIP">
                <Input
                  value={a.postal_code}
                  onChange={(e) => patchAddr(a._key, { postal_code: e.target.value })}
                />
              </FormField>
            </FormRow>
            <div className="contact-row-actions">
              <Button
                variant="ghost"
                className="btn-sm"
                onClick={() => setAddresses((rows) => rows.filter((r) => r._key !== a._key))}
              >
                Remove
              </Button>
            </div>
          </div>
        ))}
      </section>

      {/* ---------- Phones ---------- */}
      <section className="detail-card">
        <div className="section-header">
          <h2>Phones</h2>
          <Button
            variant="primary"
            className="btn-sm"
            onClick={() => setPhones((rows) => [...rows, emptyPhone()])}
          >
            Add phone
          </Button>
        </div>
        {phones.length === 0 && <p className="muted">No phones added.</p>}
        {phones.map((p) => (
          <div key={p._key} className="contact-row">
            <FormRow>
              <FormField label="Label">
                <select
                  className="form-input"
                  value={p.label}
                  onChange={(e) => patchPhone(p._key, { label: e.target.value as PersonPhoneLabel })}
                >
                  {PHONE_LABEL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Number">
                <PhoneInput
                  value={p.number}
                  onChange={(next) => patchPhone(p._key, { number: next })}
                />
              </FormField>
              <FormField label="Extension">
                <Input
                  value={p.extension}
                  onChange={(e) => patchPhone(p._key, { extension: e.target.value })}
                />
              </FormField>
              <FormField label="Primary">
                <span className="checkbox-field">
                  <input
                    type="radio"
                    name="phone-primary"
                    checked={p.is_primary}
                    onChange={() => setPhonePrimary(p._key)}
                  />
                  <span>Primary phone</span>
                </span>
              </FormField>
            </FormRow>
            <div className="contact-row-actions">
              <Button
                variant="ghost"
                className="btn-sm"
                onClick={() => setPhones((rows) => rows.filter((r) => r._key !== p._key))}
              >
                Remove
              </Button>
            </div>
          </div>
        ))}
      </section>

      {/* ---------- Emails (personal only; company-managed are read-only) ---------- */}
      <section className="detail-card">
        <div className="section-header">
          <h2>Emails</h2>
          <Button
            variant="primary"
            className="btn-sm"
            onClick={() => setEmails((rows) => [...rows, emptyEmail()])}
          >
            Add email
          </Button>
        </div>
        {managedEmails.length > 0 && (
          <p className="muted small">
            Company-managed email{managedEmails.length === 1 ? '' : 's'} (
            {managedEmails.map((e) => e.email).join(', ')}) are maintained by HR and kept on file.
          </p>
        )}
        {emails.length === 0 && <p className="muted">No personal emails added.</p>}
        {emails.map((em) => (
          <div key={em._key} className="contact-row">
            <FormRow>
              <FormField label="Label">
                <select
                  className="form-input"
                  value={em.label}
                  onChange={(e) => patchEmail(em._key, { label: e.target.value as PersonEmailLabel })}
                >
                  {EMAIL_LABEL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Email">
                <Input
                  type="email"
                  value={em.email}
                  onChange={(e) => patchEmail(em._key, { email: e.target.value })}
                />
              </FormField>
              <FormField label="Primary">
                <span className="checkbox-field">
                  <input
                    type="radio"
                    name="email-primary"
                    checked={em.is_primary}
                    onChange={() => setEmailPrimary(em._key)}
                  />
                  <span>Primary email</span>
                </span>
              </FormField>
            </FormRow>
            <div className="contact-row-actions">
              <Button
                variant="ghost"
                className="btn-sm"
                onClick={() => setEmails((rows) => rows.filter((r) => r._key !== em._key))}
              >
                Remove
              </Button>
            </div>
          </div>
        ))}
      </section>

      {/* ---------- Emergency contacts ---------- */}
      <section className="detail-card">
        <div className="section-header">
          <h2>Emergency contacts</h2>
          <Button
            variant="primary"
            className="btn-sm"
            onClick={() => setContacts((rows) => [...rows, emptyContact()])}
          >
            Add contact
          </Button>
        </div>
        {contacts.length === 0 && <p className="muted">No emergency contacts added.</p>}
        {contacts.map((c) => (
          <div key={c._key} className="contact-row">
            <FormRow>
              <FormField label="First name">
                <Input
                  value={c.first_name}
                  onChange={(e) => patchContact(c._key, { first_name: e.target.value })}
                />
              </FormField>
              <FormField label="Last name">
                <Input
                  value={c.last_name}
                  onChange={(e) => patchContact(c._key, { last_name: e.target.value })}
                />
              </FormField>
              <FormField label="Relationship">
                <select
                  className="form-input"
                  value={c.relationship}
                  onChange={(e) =>
                    patchContact(c._key, { relationship: e.target.value as EmergencyRelationship })
                  }
                >
                  {RELATIONSHIP_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Priority">
                <Input
                  type="number"
                  min={1}
                  value={c.priority}
                  onChange={(e) => patchContact(c._key, { priority: e.target.value })}
                />
              </FormField>
              <FormField label="Phone">
                <PhoneInput
                  value={c.phone}
                  onChange={(next) => patchContact(c._key, { phone: next })}
                />
              </FormField>
              <FormField label="Alt phone">
                <PhoneInput
                  value={c.alt_phone}
                  onChange={(next) => patchContact(c._key, { alt_phone: next })}
                />
              </FormField>
              <FormField label="Email" full>
                <Input
                  type="email"
                  value={c.email}
                  onChange={(e) => patchContact(c._key, { email: e.target.value })}
                />
              </FormField>
            </FormRow>
            <div className="contact-row-actions">
              <Button
                variant="ghost"
                className="btn-sm"
                onClick={() => setContacts((rows) => rows.filter((r) => r._key !== c._key))}
              >
                Remove
              </Button>
            </div>
          </div>
        ))}
      </section>

      {formError && (
        <p className="form-error error-text" role="alert">
          {formError}
        </p>
      )}

      <div className="header-actions">
        <Button variant="ghost" onClick={onDone} disabled={update.isPending}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={update.isPending}>
          {update.isPending ? 'Saving…' : 'Save contact info'}
        </Button>
      </div>
    </form>
  );
}
